import { useEffect, useState } from 'react';

/**
 * Hook genérico de debounce de valor.
 *
 * Retorna o valor recebido apenas após `delay` milissegundos sem mudanças.
 * Útil para inputs de busca, filtros etc., evitando disparar uma requisição
 * a cada tecla digitada.
 *
 * Cleanup nativo via clearTimeout previne memory leaks e atualizações em
 * componentes desmontados.
 *
 * Exemplo:
 *   const [search, setSearch] = useState('');
 *   const debouncedSearch = useDebouncedValue(search, 400);
 *   useEffect(() => { fetch(...); }, [debouncedSearch]);
 */
export function useDebouncedValue<T>(value: T, delay: number = 400): T {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

export default useDebouncedValue;
